// src/Components/VideochatView.jsx
import { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { useAtom, useAtomValue } from 'jotai'
import {
  FiMic, FiMicOff, FiVideo, FiVideoOff, FiPhoneOff, FiX, FiMinimize, FiMaximize,
  FiRepeat, FiMessageCircle, FiGift, FiChevronDown, FiMoreHorizontal, FiWifiOff,
  FiSend, FiEyeOff, FiEye, FiFlag, FiExternalLink, FiAlertTriangle,
} from 'react-icons/fi'
import useMediaQuery from '../hooks/useMediaQuery'
import config from '../Config'
import { dataUserAtom, coinsAtom } from '../Services/store/atoms'
import GiftModal, { GIFTS } from './GiftModal'
import useUserSafety from './safety/useUserSafety'
import useConnectionQuality from './videochat/useConnectionQuality'
import useZoomPan from './videochat/useZoomPan'
import useCallChat from './videochat/useCallChat'
import useCallWallet from './videochat/useCallWallet'
import CallChatOverlay from './videochat/CallChatOverlay'
import CallGiftCelebration from './videochat/CallGiftCelebration'
import { MAX_MESSAGE_LENGTH, isGiftMessage, loadRecentGiftIds, saveRecentGiftId } from './videochat/callChatUtils'

/**
 * Pone H.264 primero en las preferencias de video, para que el iPhone (y
 * Android) codifique y decodifique con el chip de video en vez del procesador:
 * con VP8 el iPhone se calienta y gasta batería de más. Cada lado envía con el
 * códec que el otro puso primero, así que esto decide lo que ENVÍA el iPhone.
 * Los demás códecs quedan después como respaldo: si el otro lado no tiene
 * H.264, la llamada sigue con VP8 como antes. No toca el SDP a mano.
 *
 * Llamar con las pistas ya agregadas (addTrack), antes de createOffer() o,
 * al contestar, después de setRemoteDescription(offer) y antes de createAnswer().
 */
function preferH264(pc) {
  try {
    if (typeof RTCRtpTransceiver === 'undefined' || !('setCodecPreferences' in RTCRtpTransceiver.prototype)) return
    const caps = RTCRtpReceiver.getCapabilities('video')
    if (!caps) return

    const isH264 = (c) => c.mimeType.toLowerCase() === 'video/h264'
    const fmtp = (c) => c.sdpFmtpLine || ''
    // Primero Constrained Baseline (42e01f) con packetization-mode=1: lo decodifica cualquier equipo.
    const rank = (c) =>
      (fmtp(c).includes('packetization-mode=1') ? 0 : 2) + (fmtp(c).includes('profile-level-id=42e01f') ? 0 : 1)

    const h264 = caps.codecs.filter(isH264).sort((a, b) => rank(a) - rank(b))
    if (h264.length === 0) return // este navegador no tiene H.264: se queda como está
    const rest = caps.codecs.filter((c) => !isH264(c))

    for (const transceiver of pc.getTransceivers()) {
      if (transceiver.receiver.track?.kind === 'video') {
        transceiver.setCodecPreferences([...h264, ...rest])
      }
    }
  } catch (err) {
    // Es una optimización: si falla, la llamada sigue con el códec por defecto.
    console.warn('[VC] preferH264:', err)
  }
}

/**
 * Props:
 *   relaySocket      — la instancia de socket.io persistente del rol activo
 *                       (signalSocketRef para la modelo, relaySocketRef para el cliente)
 *   chatId           — ID del chat activo
 *   isCaller         — true = crea el offer (quien inició la solicitud), false = crea el answer.
 *                       Sale del handshake request/accept (ver videochatAtoms.js), no del rol
 *                       de la cuenta: con roles libres, dos usuarios sin ningún `model` quedaban
 *                       ambos en false y nadie mandaba el offer.
 *   onHangUp         — callback cuando se cuelga (desde dentro del componente)
 *   authToken        — token de sesión para turn-credentials, chat y regalos
 *   partnerAvatarUrl — foto del otro usuario, para el placeholder de llamada de solo audio
 *   partnerName      — nombre del otro usuario, para el mismo placeholder
 *   partnerId        — id del otro usuario: destinatario de los regalos y de los reportes
 *   minimized        — la llamada es una burbuja arrastrable y se sigue usando la app
 *   onMinimize / onRestore — piden minimizar o volver a la pantalla completa
 *
 * Comportamiento calcado de la app iOS (docs: "Videochat: cómo funciona en la app iOS").
 */
export default function VideochatView({
  relaySocket, chatId, isCaller, onHangUp, authToken, partnerAvatarUrl, partnerName, partnerId,
  minimized = false, onMinimize, onRestore,
}) {
  const API_BASE = 'https://backend.yourpack.us/api/v1'

  // Mismo umbral que usa ChatScreen para decidir el layout (768px).
  const isMobile = useMediaQuery('(max-width: 768px)')
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  const dataUser = useAtomValue(dataUserAtom)
  const myId = dataUser?.id
  const [coinsBalance] = useAtom(coinsAtom)

  // Ventana de gracia cuando la conexión pasa a 'disconnected' (transitorio:
  // cambio de red WiFi→4G, etc.). Si no se recupera, recién ahí se cuelga.
  const GRACE_MS           = 8000
  // Si la llamada no llega a 'connected' en este tiempo, se aborta.
  const CONNECT_TIMEOUT_MS = 30000
  // Techo de bitrate del video. No es un piso ni un objetivo fijo: WebRTC baja
  // solo por debajo de esto cuando la red no lo sostiene. Igual que la app iOS:
  // 720p a 30 cuadros con techo de 2 Mbps; ante mala red baja resolución antes
  // que cuadros (maintain-framerate), porque en una videollamada pesa más la
  // fluidez que la nitidez.
  const VIDEO_MAX_BITRATE  = 2_000_000

  const pcRef               = useRef(null)
  const localStreamRef      = useRef(null)
  const localVideoRef       = useRef(null)
  const remoteVideoRef      = useRef(null)
  const graceTimerRef       = useRef(null)
  const connectTimerRef     = useRef(null)
  const endedRef            = useRef(false)   // evita terminar la llamada dos veces
  const endCallRef          = useRef(null)    // expone endCall() al render (botón colgar)
  const videoSenderRef      = useRef(null)    // RTCRtpSender del video, para cortar el envío real al pausar cámara
  const applyQualityRef     = useRef(null)    // expone applyVideoQualityCeiling() (al cambiar de cámara)

  const [isMicEnabled,    setIsMicEnabled]    = useState(true)
  const [isCameraEnabled, setIsCameraEnabled] = useState(true)
  const isCameraEnabledRef = useRef(true) // evita closures viejas al emitir el estado por socket
  const [remoteCameraOff, setRemoteCameraOff] = useState(false) // cámara del otro peer apagada: tapar con negro en vez del último frame
  const [connecting,      setConnecting]      = useState(true)
  const [reconnecting,    setReconnecting]    = useState(false) // 'disconnected' pasajero: «Reconectando…»
  const [statusMessage,   setStatusMessage]   = useState(null) // texto de error/fin

  const [showHangupConfirm, setShowHangupConfirm] = useState(false)
  // Los controles están siempre a la vista; solo el chat se abre y se cierra con su botón.
  const controlsVisible = true
  const [callSeconds,       setCallSeconds]       = useState(0)
  const [isFullscreen,      setIsFullscreen]      = useState(false)
  const [callStartedAt] = useState(() => Date.now())

  // Pantalla de la llamada
  const [swapped,   setSwapped]   = useState(false)   // un toque en mi ventanita intercambia los videos
  const [hideSelf,  setHideSelf]  = useState(false)   // «Ocultar mi imagen»: solo para mí
  const [menuOpen,  setMenuOpen]  = useState(false)
  const [toast,     setToast]     = useState(null)
  const [facing,    setFacing]    = useState('user')
  const [canSwitchCamera, setCanSwitchCamera] = useState(false)
  const switchingRef = useRef(false)

  // Chat y regalos
  const [chatOpen,     setChatOpen]     = useState(false)
  const [draft,        setDraft]        = useState('')
  const inputRef = useRef(null)
  const [giftSheetOpen, setGiftSheetOpen] = useState(false)
  const [confirmGift,   setConfirmGift]   = useState(null)
  const [celebration,   setCelebration]   = useState(null) // { gift, kind: 'sent' | 'received' }
  const [recentIds,     setRecentIds]     = useState(() => loadRecentGiftIds(myId))

  // Llamada de solo audio (ninguna de las dos cámaras activa): no tiene
  // sentido auto-ocultar los controles, porque no hay nada que "tapar" con
  // ellos — se quedan siempre visibles.
  const bothCamerasOff = !connecting && remoteCameraOff && !isCameraEnabled

  // Qué video va en grande y cuál en la ventanita. En grande va siempre la otra
  // persona, salvo que ella apague su cámara (entonces mi imagen pasa a grande
  // sola) o que yo toque mi ventanita para verme. Minimizada: solo el otro.
  const localMain = !minimized && !connecting && isCameraEnabled && !hideSelf && (remoteCameraOff || swapped)
  const remoteSmallHidden = remoteCameraOff // sin imagen del otro no hay nada que mostrar en la ventanita
  const localSmallHidden = hideSelf || (!connecting && !isCameraEnabled)

  const showToast = (text) => setToast({ text, id: Date.now() })
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 3000)
    return () => clearTimeout(t)
  }, [toast])

  const bumpControls = () => {}

  // Cronómetro de duración (arranca al conectar)
  useEffect(() => {
    if (connecting || statusMessage) return
    const interval = setInterval(() => setCallSeconds(s => s + 1), 1000)
    return () => clearInterval(interval)
  }, [connecting, statusMessage])

  const formatDuration = (secs) => {
    const h = Math.floor(secs / 3600)
    const m = Math.floor((secs % 3600) / 60)
    const s = secs % 60
    const mm = String(m).padStart(2, '0')
    const ss = String(s).padStart(2, '0')
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
  }

  // Sincroniza el estado con el modo pantalla completa nativo del navegador,
  // que también puede salir solo (tecla Esc, gesto del navegador).
  const containerRef = useRef(null)
  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen?.()
    else containerRef.current?.requestFullscreen?.()
  }

  // Wake Lock: evita que la pantalla se apague durante la llamada; se re-adquiere
  // al volver de background (el sistema lo libera al cambiar de app).
  useEffect(() => {
    let lock = null
    let disposed = false
    const request = async () => {
      try {
        lock = await navigator.wakeLock?.request('screen')
        if (disposed) lock?.release().catch(() => {})
      } catch {}
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') request()
    }
    request()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', onVisibility)
      lock?.release().catch(() => {})
    }
  }, [])

  // Al volver a la pestaña (o de la ventana flotante): en grande va el otro y se
  // muestran los controles y el chat.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      setSwapped(false)
      if (document.pictureInPictureElement) document.exitPictureInPicture?.().catch(() => {})
      bumpControls()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Al cambiar de pestaña el video del otro puede seguir en una ventana flotante
  // (donde el navegador lo permite). Al colgar se cierra (teardownPeer).
  useEffect(() => {
    if (!('mediaSession' in navigator)) return
    try {
      navigator.mediaSession.setActionHandler('enterpictureinpicture', () => {
        const v = remoteVideoRef.current
        if (v && document.pictureInPictureEnabled && !document.pictureInPictureElement) {
          v.requestPictureInPicture().catch(() => {})
        }
      })
    } catch {}
    return () => { try { navigator.mediaSession.setActionHandler('enterpictureinpicture', null) } catch {} }
  }, [])

  // Botón/gesto "atrás" del navegador durante la llamada: en vez de navegar (y
  // cortar la llamada al desmontar), pedir confirmación con el mismo modal.
  useEffect(() => {
    window.history.pushState({ videochat: true }, '')
    const onPopState = () => {
      window.history.pushState({ videochat: true }, '')
      setShowHangupConfirm(true)
    }
    window.addEventListener('popstate', onPopState)
    return () => {
      window.removeEventListener('popstate', onPopState)
      // retirar la entrada centinela para no dejar historial fantasma
      if (window.history.state?.videochat) window.history.back()
    }
  }, [])

  // ¿Hay otra cámara para cambiar? En móviles siempre (frontal ↔ trasera).
  useEffect(() => {
    if (connecting) return
    navigator.mediaDevices?.enumerateDevices?.().then((devices) => {
      const cams = devices.filter((d) => d.kind === 'videoinput').length
      setCanSwitchCamera(cams > 1 || /Android|iPhone|iPad/i.test(navigator.userAgent))
    }).catch(() => {})
  }, [connecting])

  // ── Calidad de la red (cada 2 s, con histéresis de 2 muestras) ──
  const weakConnection = useConnectionQuality(pcRef, !connecting && !reconnecting && !statusMessage)

  // ── Chat, saldo y regalos ──
  // Los mensajes se ven sobre el video; un toque en el video los oculta y otro los
  // vuelve a mostrar. Con el chat abierto siempre se ven.
  const [chatTextHidden, setChatTextHidden] = useState(false)
  const chatShown = !minimized && !connecting && (chatOpen || !chatTextHidden)
  const chat = useCallChat({ chatId, myId, authToken, socket: relaySocket, visible: chatShown })
  const wallet = useCallWallet({ authToken, messages: chat.messages, myId, callStartedAt })

  const partnerForSafety = useMemo(() => (partnerId ? { id: partnerId, nickname: partnerName } : null), [partnerId, partnerName])
  const safety = useUserSafety({ user: partnerForSafety, canBlock: false })

  // El otro me hizo un regalo: lo veo celebrar sobre el video.
  useEffect(() => {
    if (!relaySocket) return
    const onGift = ({ gift }) => { if (gift) setCelebration({ gift, kind: 'received' }) }
    relaySocket.on('gift-celebration', onGift)
    return () => relaySocket.off('gift-celebration', onGift)
  }, [relaySocket])

  const afterGiftSent = async (gift) => {
    if (!gift) return
    const fresh = await chat.refetch()
    // El servidor crea el mensaje del regalo al cobrarlo: se avisa a la otra
    // persona por el relay (no se manda uno propio, saldrían dos).
    const mine = (fresh || []).filter((m) => isGiftMessage(m) && chat.isMine(m)).pop()
    try {
      if (mine) relaySocket.emit('new-message', { chatId, message: mine })
      relaySocket.emit('gift-celebration', { chatId, gift: { id: gift.id, name: gift.name, coins: gift.coins, emoji: gift.emoji, img: '' } })
    } catch {}
    setRecentIds(saveRecentGiftId(myId, gift.id))
    setCelebration({ gift, kind: 'sent' })
    wallet.refresh()
  }

  const sendQuickGift = async (gift) => {
    setConfirmGift(null)
    try {
      await axios.post(`${config.REACT_APP_BASE_URL}/api/v1/chat/gift-coins`, {
        recipient_id: partnerId, chat_id: chatId, coins: gift.coins, gift_emoji: gift.emoji, gift_name: gift.name,
      }, { headers: { Authorization: `Bearer ${authToken}` } })
      await afterGiftSent(gift)
    } catch (err) {
      try { navigator.vibrate?.([60, 40, 60]) } catch {}
      showToast(err?.response?.data?.error || 'No se pudo enviar el regalo.')
    }
  }

  // Últimos 3 regalos enviados (o los 3 primeros del catálogo si aún no envió ninguno).
  const quickGifts = useMemo(() => {
    const recent = recentIds.map((id) => GIFTS.find((g) => g.id === id)).filter(Boolean)
    return recent.length ? recent : GIFTS.slice(0, 3)
  }, [recentIds])

  const openGiftSheet = async () => {
    inputRef.current?.blur() // baja el teclado antes de abrir la hoja
    setMenuOpen(false)
    await wallet.refresh()
    setGiftSheetOpen(true)
  }

  const openChat = () => { setChatOpen(true); setTimeout(() => inputRef.current?.focus(), 50) }
  const closeChat = () => {
    // Cerrar siempre baja el teclado primero: nunca queda un teclado sin forma de ocultarlo.
    inputRef.current?.blur()
    chat.emitStopTyping()
    setChatOpen(false)
  }

  const submitDraft = async () => {
    const result = await chat.send(draft)
    if (result.blocked) {
      try { navigator.vibrate?.([60, 40, 60]) } catch {}
      window.alert('No permitido\nNo se permiten correos ni teléfonos.')
      return
    }
    if (result.ok) { setDraft(''); closeChat() } // al enviar se vuelve al estado inicial
  }

  // ── Mi ventanita: arrastrable a las 4 esquinas con inercia, o burbuja minimizada ──
  const PIP_SIZES = {
    normal: { w: 112, h: 160 },
    min:    { w: 56,  h: 56 },
  }
  const PIP_MARGIN = 12
  const TOP_RESERVED = 92     // barra de arriba: saldo, nombre, menú
  const BOTTOM_RESERVED = 130 // controles + zona segura
  const NOTICE_HEIGHT = 56    // con un aviso arriba la ventanita baja para no taparlo
  const pipRef        = useRef(null)
  const dragRef       = useRef(null)
  const [pipMode,   setPipMode]   = useState('normal') // 'normal' | 'min'
  const [pipCorner, setPipCorner] = useState('br')     // tl | tr | bl | br
  const [pipDrag,   setPipDrag]   = useState(null)     // {x,y} mientras se arrastra
  const [, setLayoutTick] = useState(0)
  useEffect(() => {
    const onResize = () => setLayoutTick((n) => n + 1)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const hasNotice = reconnecting || weakConnection
  const cornerPos = (corner, mode = pipMode) => {
    const c = containerRef.current
    const W = c?.clientWidth ?? window.innerWidth
    const H = c?.clientHeight ?? window.innerHeight
    const { w, h } = PIP_SIZES[mode]
    const top = TOP_RESERVED + (hasNotice ? NOTICE_HEIGHT : 0)
    const bottom = BOTTOM_RESERVED
    return {
      tl: { x: PIP_MARGIN,               y: top },
      tr: { x: W - w - PIP_MARGIN,       y: top },
      bl: { x: PIP_MARGIN,               y: H - h - bottom },
      br: { x: W - w - PIP_MARGIN,       y: H - h - bottom },
    }[corner]
  }

  // Con el chat abierto la ventanita sube arriba para no tapar los mensajes.
  const effectiveCorner = chatOpen ? (pipCorner[1] === 'l' ? 'tl' : 'tr') : pipCorner

  const handlePipPointerDown = (e) => {
    const c = containerRef.current
    const pip = pipRef.current
    if (!c || !pip) return
    e.stopPropagation()
    const rect = pip.getBoundingClientRect()
    const cRect = c.getBoundingClientRect()
    dragRef.current = {
      offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top,
      cLeft: cRect.left, cTop: cRect.top,
      startX: e.clientX, startY: e.clientY,
      moved: false, vx: 0, vy: 0, lastX: e.clientX, lastY: e.clientY, lastT: performance.now(),
    }
    try { pip.setPointerCapture(e.pointerId) } catch {}
  }

  const handlePipPointerMove = (e) => {
    const d = dragRef.current
    if (!d) return
    if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) < 6) return
    d.moved = true
    const now = performance.now()
    const dt = Math.max(1, now - d.lastT) / 1000
    d.vx = 0.6 * d.vx + 0.4 * ((e.clientX - d.lastX) / dt)
    d.vy = 0.6 * d.vy + 0.4 * ((e.clientY - d.lastY) / dt)
    d.lastX = e.clientX; d.lastY = e.clientY; d.lastT = now
    setPipDrag({ x: e.clientX - d.cLeft - d.offsetX, y: e.clientY - d.cTop - d.offsetY })
  }

  const handlePipPointerUp = (e) => {
    const d = dragRef.current
    dragRef.current = null
    if (!d) return
    e.stopPropagation()
    if (!d.moved) {
      // Tap: la burbuja se restaura; la ventanita intercambia los videos.
      if (pipMode === 'min') changePipMode('normal')
      else if (isCameraEnabled || localMain) { setSwapped(s => !s); setHideSelf(false) }
      return
    }
    // Va a la esquina hacia donde se lanzó: se usa la posición proyectada con la inercia.
    const cur = pipDrag ?? cornerPos(effectiveCorner)
    const projX = cur.x + d.vx * 0.2
    const projY = cur.y + d.vy * 0.2
    const candidates = chatOpen ? ['tl', 'tr'] : ['tl', 'tr', 'bl', 'br']
    let best = candidates[0]
    let bestDist = Infinity
    for (const k of candidates) {
      const p = cornerPos(k)
      const dist = Math.hypot(p.x - projX, p.y - projY)
      if (dist < bestDist) { bestDist = dist; best = k }
    }
    if (best !== effectiveCorner) { try { navigator.vibrate?.(10) } catch {} }
    pipBouncyRef.current = Math.hypot(d.vx, d.vy) > 300 // rebota un poco solo si se lanzó rápido
    setPipCorner(best)
    setPipDrag(null)
  }
  const pipBouncyRef = useRef(false)

  const changePipMode = (mode) => { setPipMode(mode) }

  const pipHandlers = {
    onPointerDown: handlePipPointerDown,
    onPointerMove: handlePipPointerMove,
    onPointerUp: handlePipPointerUp,
    onPointerCancel: handlePipPointerUp,
  }

  // ── Burbuja de la llamada minimizada (se sigue usando la app) ──
  const BUBBLE = { w: 112, h: 152 }
  const bubbleDragRef = useRef(null)
  const [bubblePos, setBubblePos] = useState(null)
  const bubbleXY = bubblePos ?? { x: window.innerWidth - BUBBLE.w - 12, y: window.innerHeight - BUBBLE.h - 110 }
  const handleBubbleDown = (e) => {
    e.stopPropagation()
    bubbleDragRef.current = { ox: e.clientX - bubbleXY.x, oy: e.clientY - bubbleXY.y, sx: e.clientX, sy: e.clientY, moved: false }
    try { e.currentTarget.setPointerCapture(e.pointerId) } catch {}
  }
  const handleBubbleMove = (e) => {
    const d = bubbleDragRef.current
    if (!d) return
    if (!d.moved && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < 6) return
    d.moved = true
    setBubblePos({
      x: Math.min(Math.max(8, e.clientX - d.ox), window.innerWidth - BUBBLE.w - 8),
      y: Math.min(Math.max(8, e.clientY - d.oy), window.innerHeight - BUBBLE.h - 8),
    })
  }
  const handleBubbleUp = () => {
    const d = bubbleDragRef.current
    bubbleDragRef.current = null
    if (d && !d.moved) onRestore?.() // un toque vuelve a la llamada
  }

  // ── Zoom del video del otro + toques del fondo ──
  const zoomWrapRef = useRef(null)
  const handleBackgroundTap = () => {
    if (chatOpen) { closeChat(); return }
    if (menuOpen) { setMenuOpen(false); return }
    setChatTextHidden(h => !h) // un toque en el video oculta o muestra los mensajes
  }
  const zoomEnabled = !minimized && !connecting && !localMain
  const zoom = useZoomPan({
    containerRef, videoRef: remoteVideoRef, wrapRef: zoomWrapRef,
    enabled: zoomEnabled, reduceMotion,
    onTap: handleBackgroundTap,
    onDoubleTap: () => switchCamera(),
  })

  // Al minimizar o intercambiar, el video del otro vuelve a cuadro completo.
  useEffect(() => { if (minimized || localMain) zoom.reset(false) }, [minimized, localMain]) // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    let cancelled = false

    // Señalización que llega ANTES de que exista el RTCPeerConnection (el otro
    // lado terminó su setup más rápido, típico en la 2da llamada con permisos y
    // conexiones ya calientes). Antes se descartaba y la llamada nunca conectaba;
    // ahora se encola y se procesa apenas el peer está listo.
    let pendingOffer = null
    const pendingIce = []

    const clearTimers = () => {
      if (graceTimerRef.current)   { clearTimeout(graceTimerRef.current);   graceTimerRef.current = null }
      if (connectTimerRef.current) { clearTimeout(connectTimerRef.current); connectTimerRef.current = null }
    }

    // Cierre limpio del RTCPeerConnection (patrón closeVideoCall de MDN):
    // anular los handlers ANTES de close() para evitar callbacks tardíos y
    // detener todos los tracks para apagar la cámara/mic (la luz del device).
    const teardownPeer = () => {
      const pc = pcRef.current
      if (pc) {
        pc.ontrack                      = null
        pc.onicecandidate               = null
        pc.oniceconnectionstatechange   = null
        pc.onconnectionstatechange      = null
        pc.onnegotiationneeded          = null
        try { pc.close() } catch {}
      }
      pcRef.current = null
      localStreamRef.current?.getTracks().forEach(t => t.stop())
      localStreamRef.current = null
      // Igual que la app al colgar: cerrar la ventana flotante (PiP) y borrar el
      // último cuadro. Con solo srcObject = null el navegador puede dejar la
      // miniatura abierta con la última imagen del otro congelada.
      if (document.pictureInPictureElement) document.exitPictureInPicture?.().catch(() => {})
      for (const ref of [remoteVideoRef, localVideoRef]) {
        const video = ref.current
        if (!video) continue
        try { video.pause() } catch {}
        video.srcObject = null
        video.removeAttribute('src')
        try { video.load() } catch {}
      }
    }

    // Termina la llamada UNA sola vez: avisa al peer, limpia y notifica al padre.
    // Si recibe `message`, lo muestra brevemente antes de desmontar.
    const endCall = (message) => {
      if (endedRef.current) return
      endedRef.current = true
      clearTimers()
      try { relaySocket.emit('videochat-end', { chatId }) } catch {}
      teardownPeer()
      if (message) {
        setStatusMessage(message)
        setConnecting(true) // reutiliza el overlay para mostrar el mensaje
        setTimeout(() => { if (!cancelled) onHangUp() }, 1500)
      } else {
        onHangUp()
      }
    }
    endCallRef.current = endCall

    // Avisar al otro peer si se cierra/recarga la ventana (best-effort: llega
    // más rápido que el 'disconnect' del socket en el backend).
    const handleBeforeUnload = () => {
      try { relaySocket.emit('videochat-end', { chatId }) } catch {}
    }
    window.addEventListener('beforeunload', handleBeforeUnload)

    // ── Signaling handlers (registrar ANTES del setup async para evitar race) ──

    // ICE encolados: se agregan recién cuando hay remoteDescription.
    const flushPendingIce = async () => {
      const pc = pcRef.current
      if (!pc || !pc.remoteDescription) return
      while (pendingIce.length > 0) {
        const candidate = pendingIce.shift()
        try { await pc.addIceCandidate(new RTCIceCandidate(candidate)) } catch {}
      }
    }

    const handleOffer = async ({ sdp }) => {
      const pc = pcRef.current
      if (!pc) { pendingOffer = sdp; return }
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(sdp))
        preferH264(pc)
        const answer = await pc.createAnswer()
        await pc.setLocalDescription(answer)
        relaySocket.emit('webrtc-answer', { chatId, sdp: answer })
        await flushPendingIce()
      } catch (err) { console.error('[VC] handleOffer:', err) }
    }

    const handleAnswer = async ({ sdp }) => {
      const pc = pcRef.current
      if (!pc) return
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(sdp))
        await flushPendingIce()
      } catch (err) { console.error('[VC] handleAnswer:', err) }
    }

    const handleIce = async ({ candidate }) => {
      if (!candidate) return
      const pc = pcRef.current
      if (!pc || !pc.remoteDescription) { pendingIce.push(candidate); return }
      try { await pc.addIceCandidate(new RTCIceCandidate(candidate)) } catch {}
    }

    // Estado de cámara del otro peer: reemplaza el fallback nativo de WebRTC
    // (que en varios navegadores deja el último frame congelado en vez de
    // negro) por un overlay negro explícito controlado por señalización.
    const handleCameraState = ({ enabled }) => setRemoteCameraOff(!enabled)
    relaySocket.on('videochat-camera-state', handleCameraState)

    if (!isCaller) relaySocket.on('webrtc-offer',  handleOffer)
    if (isCaller)  relaySocket.on('webrtc-answer', handleAnswer)
    relaySocket.on('webrtc-ice', handleIce)

    // Fija el techo de bitrate y la política de degradado del video saliente.
    // Se llama dos veces a propósito: Chrome puebla `encodings` apenas se hace
    // addTrack, pero Firefox recién después de negociar, así que se reintenta al
    // conectar. setParameters es idempotente, aplicarlo de nuevo no molesta.
    const applyVideoQualityCeiling = async () => {
      const sender = videoSenderRef.current
      if (!sender) return
      try {
        const params = sender.getParameters()
        if (!params.encodings?.length) return
        // 'maintain-framerate': en videollamada la fluidez pesa más que la
        // nitidez, así que al degradar baja resolución antes que FPS.
        params.degradationPreference = 'maintain-framerate'
        params.encodings[0].maxBitrate = VIDEO_MAX_BITRATE
        await sender.setParameters(params)
      } catch (err) {
        console.warn('[VC] no se pudo fijar el techo de bitrate:', err)
      }
    }

    applyQualityRef.current = applyVideoQualityCeiling

    // ── Setup async ────────────────────────────────────────────────────────────
    ;(async () => {
      try {
        // 1. TURN credentials desde el backend
        const res = await fetch(`${API_BASE}/chat/videochat/${chatId}/turn-credentials`, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        if (!res.ok) throw new Error('TURN credentials failed')
        const { iceServers } = await res.json()
        if (cancelled) return

        // Cloudflare devuelve objeto, RTCPeerConnection necesita array
        const iceServersArr = Array.isArray(iceServers) ? iceServers : [iceServers]

        // 2. Cámara + micrófono
        // 720p a 30 cuadros, igual que la app iOS: con solo `ideal` el navegador
        // entrega lo mejor que pueda y WebRTC negocia hacia abajo cuando hace falta.
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: {
            facingMode: 'user',
            width:     { ideal: 1280 },
            height:    { ideal: 720 },
            frameRate: { ideal: 30 },
          },
        })
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return }

        localStreamRef.current = stream
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream
          localVideoRef.current.muted = true   // nunca retroalimentar el propio audio
        }

        // 3. RTCPeerConnection
        const pc = new RTCPeerConnection({ iceServers: iceServersArr })
        pcRef.current = pc

        stream.getTracks().forEach(track => {
          const sender = pc.addTrack(track, stream)
          if (track.kind === 'video') videoSenderRef.current = sender
        })

        await applyVideoQualityCeiling()

        pc.ontrack = (event) => {
          if (cancelled) return
          const remote = event.streams?.[0]
          if (remote && remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = remote
            setConnecting(false)
          }
        }

        // Estado agregado de la conexión (recomendado sobre iceConnectionState).
        pc.onconnectionstatechange = () => {
          if (cancelled || endedRef.current) return
          const s = pc.connectionState
          if (s === 'connected') {
            setConnecting(false)
            setReconnecting(false)
            clearTimers() // ya conectó: cancelar timeout de conexión y de gracia
            applyVideoQualityCeiling() // reintento para navegadores que pueblan `encodings` recién al negociar
            // Por si el otro peer ya tenía la cámara apagada antes de conectar.
            relaySocket.emit('videochat-camera-state', { chatId, enabled: isCameraEnabledRef.current })
          } else if (s === 'disconnected') {
            // Transitorio: «Reconectando…» al instante y ventana de gracia antes de colgar.
            setReconnecting(true)
            if (!graceTimerRef.current) {
              graceTimerRef.current = setTimeout(() => {
                graceTimerRef.current = null
                if (pcRef.current?.connectionState !== 'connected') {
                  endCall('Se perdió la conexión')
                }
              }, GRACE_MS)
            }
          } else if (s === 'failed') {
            endCall('Se perdió la conexión')
          }
          // 'closed' lo gestiona el cleanup del efecto
        }

        pc.onicecandidate = (event) => {
          if (event.candidate) {
            relaySocket.emit('webrtc-ice', { chatId, candidate: event.candidate })
          }
        }

        // Timeout global: si no conecta en CONNECT_TIMEOUT_MS, abortar (evita
        // el spinner infinito cuando el ICE nunca completa, p.ej. NAT simétrico).
        connectTimerRef.current = setTimeout(() => {
          connectTimerRef.current = null
          if (pcRef.current?.connectionState !== 'connected') {
            endCall('No se pudo conectar')
          }
        }, CONNECT_TIMEOUT_MS)

        // 4. Caller (quien inició la solicitud) crea y envía el offer
        if (isCaller) {
          preferH264(pc)
          const offer = await pc.createOffer()
          await pc.setLocalDescription(offer)
          relaySocket.emit('webrtc-offer', { chatId, sdp: offer })
        } else if (pendingOffer) {
          // El offer llegó mientras este lado aún hacía TURN/getUserMedia:
          // procesarlo ahora que el peer existe.
          const sdp = pendingOffer
          pendingOffer = null
          await handleOffer({ sdp })
        }
      } catch (err) {
        if (!cancelled) { console.error('[VC] setup error:', err); endCall('No se pudo conectar') }
      }
    })()

    return () => {
      cancelled = true
      window.removeEventListener('beforeunload', handleBeforeUnload)
      clearTimers()
      relaySocket.off('webrtc-offer',  handleOffer)
      relaySocket.off('webrtc-answer', handleAnswer)
      relaySocket.off('webrtc-ice',    handleIce)
      relaySocket.off('videochat-camera-state', handleCameraState)
      teardownPeer()
      if (document.fullscreenElement) document.exitFullscreen?.()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMic = () => {
    localStreamRef.current?.getAudioTracks().forEach(t => { t.enabled = !t.enabled })
    setIsMicEnabled(p => !p)
  }

  const toggleCamera = () => {
    const track = localStreamRef.current?.getVideoTracks()[0]
    const next = !isCameraEnabledRef.current
    if (track) track.enabled = next

    // No alcanza con track.enabled=false: WebRTC igual manda frames negros
    // codificados (poco peso pero no cero). replaceTrack(null) corta el envío
    // por completo mientras la cámara está pausada; replaceTrack(track) lo
    // retoma sin renegociar la conexión.
    videoSenderRef.current?.replaceTrack(next ? track : null).catch(() => {})

    isCameraEnabledRef.current = next
    setIsCameraEnabled(next)
    if (!next) setSwapped(false)
    try { relaySocket.emit('videochat-camera-state', { chatId, enabled: next }) } catch {}
  }

  // Frontal ↔ trasera: getUserMedia con otro facingMode + replaceTrack.
  const switchCamera = async () => {
    if (!canSwitchCamera || switchingRef.current) return
    if (!isCameraEnabledRef.current) { showToast('Enciende la cámara para cambiarla'); return }
    switchingRef.current = true
    const next = facing === 'user' ? 'environment' : 'user'
    const constraints = { video: { facingMode: { ideal: next }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } } }
    const stream = localStreamRef.current
    const oldTrack = stream?.getVideoTracks()[0]
    try {
      let fresh
      try {
        fresh = await navigator.mediaDevices.getUserMedia(constraints)
      } catch (err) {
        // Algunos móviles no dejan abrir dos cámaras a la vez: se suelta la actual y se reintenta.
        if (err?.name === 'NotReadableError' || err?.name === 'AbortError') {
          oldTrack?.stop()
          fresh = await navigator.mediaDevices.getUserMedia(constraints)
        } else throw err
      }
      const newTrack = fresh.getVideoTracks()[0]
      await videoSenderRef.current?.replaceTrack(newTrack)
      if (stream && oldTrack) { stream.removeTrack(oldTrack); oldTrack.stop() }
      stream?.addTrack(newTrack)
      if (localVideoRef.current) localVideoRef.current.srcObject = stream
      setFacing(next)
      applyQualityRef.current?.()
      bumpControls()
    } catch (err) {
      console.warn('[VC] cambiar de cámara:', err)
      showToast('No se pudo cambiar de cámara')
    } finally {
      switchingRef.current = false
    }
  }

  // Colgado manual: pasa por el mismo endCall() (avisa al peer + limpia + onHangUp).
  const hangUp = () => { endCallRef.current?.(null) }

  const toggleHideSelf = () => {
    const next = !hideSelf
    setHideSelf(next)
    if (next) { setSwapped(false); showToast('Tu imagen está oculta para ti. La otra persona te sigue viendo.') }
    setMenuOpen(false)
  }

  const enterFloatingWindow = () => {
    setMenuOpen(false)
    const v = remoteVideoRef.current
    if (v && document.pictureInPictureEnabled && !document.pictureInPictureElement) v.requestPictureInPicture().catch(() => {})
  }

  const unseenBadge = chat.unseen > 99 ? '99+' : chat.unseen
  const showChatBlock = !minimized && !connecting && !showHangupConfirm

  // ── Ventanita (la que no está en grande) ──
  const smallBase = (hidden, mode) => (
    hidden
      ? 'hidden'
      : `absolute overflow-hidden select-none z-20 cursor-grab active:cursor-grabbing ${
          mode === 'min'
            ? 'rounded-full flex items-center justify-center bg-white/20 hover:bg-white/30'
            : 'rounded-xl bg-gray-800 shadow-lg'
        }`
  )
  const smallStyle = () => {
    const { w, h } = PIP_SIZES[pipMode]
    const pos = pipDrag ?? cornerPos(effectiveCorner)
    const bounce = pipBouncyRef.current ? 'cubic-bezier(0.34, 1.56, 0.64, 1)' : 'cubic-bezier(0.2, 0.9, 0.3, 1)'
    return {
      width: w, height: h, left: pos.x, top: pos.y,
      touchAction: 'none', // sin esto iOS scrollea en vez de arrastrar
      transition: pipDrag || reduceMotion ? 'none' : `left 0.35s ${bounce}, top 0.35s ${bounce}`,
    }
  }
  const minimizeButton = (
    <div className="absolute top-1 right-1 flex gap-1">
      <button
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); changePipMode('min') }}
        className="w-6 h-6 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center"
        aria-label="Minimizar mi ventanita"
      >
        <FiX size={12} className="text-white" />
      </button>
    </div>
  )

  // Contenedores de los dos videos: el grande ocupa todo; el otro es la ventanita.
  const remoteIsMain = minimized || !localMain
  const remoteContainerClass = minimized
    ? 'absolute inset-0 z-[1]'
    : remoteIsMain ? 'absolute inset-0 z-[1]' : smallBase(remoteSmallHidden, pipMode)
  const localContainerClass = minimized
    ? 'hidden'
    : !remoteIsMain ? 'absolute inset-0 z-[1]' : smallBase(localSmallHidden, pipMode)
  const remoteSmall = !remoteIsMain && !remoteSmallHidden
  const localSmall = remoteIsMain && !localSmallHidden && !minimized

  const circleBtn = 'w-12 h-12 max-[380px]:w-11 max-[380px]:h-11 shrink-0 rounded-full flex items-center justify-center transition-[transform,background-color,opacity] duration-200 ease-[cubic-bezier(0.32,0.72,0,1)] active:scale-90 backdrop-blur-xl backdrop-saturate-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
  const btnOn = 'bg-white text-black'     // encendido/activo: casi blanco con ícono negro
  const btnOff = 'bg-[rgba(28,28,30,0.72)] hover:bg-[rgba(44,44,46,0.8)] text-white'

  const topInset = 'calc(env(safe-area-inset-top, 0px) + 10px)'
  const controlsFade = controlsVisible || connecting ? 'opacity-100' : 'opacity-0 pointer-events-none'

  return (
    <div
      ref={containerRef}
      className={
        minimized
          ? 'fixed overflow-hidden bg-black rounded-2xl shadow-2xl cursor-grab'
          : 'relative w-full h-full bg-black flex items-center justify-center overflow-hidden'
      }
      style={minimized
        ? { left: bubbleXY.x, top: bubbleXY.y, width: BUBBLE.w, height: BUBBLE.h, pointerEvents: 'auto', touchAction: 'none', zIndex: 10010 }
        : undefined}
      {...(minimized ? { onPointerDown: handleBubbleDown, onPointerMove: handleBubbleMove, onPointerUp: handleBubbleUp, onPointerCancel: handleBubbleUp } : {})}
    >

      {/* Video remoto — a pantalla completa en su formato original (contain: si es
           horizontal se ve horizontal, sin recortar). Cuando mi imagen va en grande
           pasa a ser la ventanita. */}
      <div
        ref={remoteIsMain ? undefined : pipRef}
        className={remoteContainerClass}
        style={remoteSmall ? smallStyle() : undefined}
        {...(remoteSmall ? pipHandlers : {})}
      >
        <div ref={zoomWrapRef} className="absolute inset-0 will-change-transform">
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className={`w-full h-full ${remoteIsMain ? 'object-contain' : 'object-cover'} ${remoteSmall && pipMode === 'min' ? 'hidden' : ''}`}
          />
        </div>
        {remoteSmall && (pipMode === 'min' ? <FiVideo size={22} className="text-white" /> : minimizeButton)}
      </div>

      {/* Mi cámara: ventanita arrastrable (o a pantalla completa si va en grande) */}
      <div
        ref={remoteIsMain ? pipRef : undefined}
        className={localContainerClass}
        style={localSmall ? smallStyle() : undefined}
        {...(localSmall ? pipHandlers : {})}
      >
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full ${facing === 'user' ? 'scale-x-[-1]' : ''} ${  // espejo solo con la cámara frontal
            localMain
              ? (isMobile ? 'object-cover' : 'object-contain')
              : `object-cover ${pipMode === 'min' ? 'hidden' : ''}`
          }`}
        />
        {localSmall && (pipMode === 'min' ? <FiVideo size={22} className="text-white" /> : minimizeButton)}
      </div>

      {/* Sin cámara del otro: foto y nombre (burbuja) o llamada de solo audio */}
      {(minimized ? remoteCameraOff && !connecting : bothCamerasOff) && (
        <div className="absolute inset-0 z-[2] bg-gray-900 flex flex-col items-center justify-center gap-3 pointer-events-none">
          {partnerAvatarUrl ? (
            <img src={partnerAvatarUrl} alt={partnerName || 'avatar'} className={`${minimized ? 'w-12 h-12' : 'w-24 h-24'} rounded-full object-cover`} />
          ) : (
            <div className={`${minimized ? 'w-12 h-12' : 'w-24 h-24'} rounded-full bg-gray-700 flex items-center justify-center`}>
              {partnerName
                ? <span className={`text-white ${minimized ? 'text-lg' : 'text-3xl'} font-semibold`}>{partnerName.charAt(0).toUpperCase()}</span>
                : <FiVideoOff size={28} className="text-white/40" />}
            </div>
          )}
          {!minimized && partnerName && <p className="text-white text-base font-medium">{partnerName}</p>}
          {!minimized && <p className="text-white/70 text-sm">Llamada de audio</p>}
        </div>
      )}

      {/* La otra persona apagó su cámara y su video está en grande: se tapa con negro y
           un ícono de cámara tachada (con solo enabled=false puede quedar un cuadro congelado) */}
      {!minimized && !connecting && remoteIsMain && remoteCameraOff && !bothCamerasOff && (
        <div className="absolute inset-0 z-[2] bg-black flex items-center justify-center pointer-events-none">
          <FiVideoOff size={40} className="text-white/40" aria-label="La otra persona apagó su cámara" />
        </div>
      )}

      {/* Burbuja minimizada: micrófono silenciado y red débil */}
      {minimized && (
        <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between pointer-events-none z-[5]">
          <span>{!isMicEnabled && <FiMicOff size={14} className="text-red-400 drop-shadow" aria-label="Micrófono silenciado" />}</span>
          <span>{(weakConnection || reconnecting) && <FiWifiOff size={14} className="text-amber-300 drop-shadow" aria-label="Conexión débil" />}</span>
        </div>
      )}

      {/* Capa de gestos sobre el video: un toque muestra u oculta los controles,
           doble toque cambia de cámara, pellizco hace zoom y un dedo recorre el
           video con zoom. Va debajo de los controles y de mi ventanita, así que
           los toques sobre ellos nunca llegan acá. */}
      {!minimized && (
        <div
          className="absolute inset-0 z-[3]"
          style={{ touchAction: 'none' }}
          {...(localMain
            ? { onClick: handleBackgroundTap }
            : zoom.handlers)}
        />
      )}

      {/* Overlay de estado cuando no hay video remoto aún o terminó la llamada */}
      {!minimized && connecting && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 z-10 gap-3">
          {statusMessage ? (
            <p className="text-white text-lg">{statusMessage}</p>
          ) : (
            <>
              {partnerAvatarUrl ? (
                <img src={partnerAvatarUrl} alt={partnerName || 'avatar'} className="w-24 h-24 rounded-full object-cover" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-700 flex items-center justify-center">
                  <span className="text-white text-3xl font-semibold">{partnerName ? partnerName.charAt(0).toUpperCase() : ''}</span>
                </div>
              )}
              {partnerName && <p className="text-white text-base font-medium">{partnerName}</p>}
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <p className="text-white text-base">Conectando…</p>
              </div>
            </>
          )}
        </div>
      )}

      {!minimized && (<>
        {/* Degradados: la hora, el chat y los controles se leen sobre cualquier imagen */}
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 z-[4] bg-gradient-to-b from-black/60 to-transparent transition-opacity duration-300 ${controlsVisible || chatOpen ? 'opacity-100' : 'opacity-0'}`}
          style={{ height: 'calc(90px + env(safe-area-inset-top, 0px))' }}
        />
        <div
          className={`pointer-events-none absolute inset-x-0 bottom-0 z-[4] bg-gradient-to-t from-black/70 to-transparent transition-opacity duration-300 ${controlsVisible || chatOpen ? 'opacity-100' : 'opacity-0'}`}
          style={{ height: 320 }}
        />

        {/* Barra de arriba, como en iOS: ⌄ y saldo a la izquierda, nombre y cronómetro
             al centro, «…» a la derecha; debajo, «+N en esta llamada» y «Silenciado». */}
        {!connecting && (
          <div className="absolute inset-x-0 top-0 z-20 pointer-events-none grid grid-cols-[1fr_auto_1fr] items-start gap-2 px-4" style={{ paddingTop: topInset }}>
            <div className="flex flex-col items-start gap-2 pointer-events-auto min-w-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onMinimize?.()}
                  className={`${circleBtn} ${btnOff} !w-11 !h-11`}
                  aria-label="Minimizar la llamada"
                >
                  <FiChevronDown size={22} />
                </button>
                <button
                  onClick={() => { setConfirmGift(null); openGiftSheet() }}
                  className={`flex items-center gap-1.5 rounded-full ${btnOff} backdrop-blur-xl px-3 text-white text-[17px] font-semibold tabular-nums transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${wallet.pulse ? 'scale-[1.12]' : 'scale-100'}`}
                  aria-label={`Saldo: ${wallet.coins ?? 'cargando'} coins. Abrir regalos`}
                  style={{ height: 44 }}
                >
                  <span aria-hidden="true" className="flex h-5 w-5 items-center justify-center rounded-full bg-[#FFCC00] text-[11px] font-bold text-black">C</span>
                  <span>{wallet.coins ?? '—'}</span>
                  {wallet.delta > 0 && <span className="text-[#FFCC00]">+{wallet.delta}</span>}
                </button>
              </div>
              {wallet.receivedInCall > 0 && (
                <div className="flex items-center gap-1 text-[13px] font-semibold text-[#FFCC00] pointer-events-none" style={{ textShadow: '0 1px 3px rgba(0,0,0,.7)' }}>
                  <span aria-hidden="true">🎁</span> +{wallet.receivedInCall} en esta llamada
                </div>
              )}
              {!isMicEnabled && (
                <button
                  onClick={() => toggleMic()}
                  className="flex items-center gap-1.5 text-[15px] font-semibold text-white"
                  aria-label="Micrófono silenciado. Activar"
                  style={{ minHeight: 32, textShadow: '0 1px 3px rgba(0,0,0,.7)' }}
                >
                  <FiMicOff size={16} className="text-[#FF453A]" /> Silenciado
                </button>
              )}
            </div>

            <div className="flex flex-col items-center text-white pt-0.5" style={{ textShadow: '0 1px 3px rgba(0,0,0,.6)' }}>
              {partnerName && <span className="text-[17px] font-semibold leading-tight max-w-[40vw] truncate">{partnerName}</span>}
              <span className="text-[13px] font-medium tabular-nums text-white/85">{formatDuration(callSeconds)}</span>
            </div>

            {/* Derecha: menú «…» */}
            <div className="relative pointer-events-auto justify-self-end">
              <button
                onClick={() => setMenuOpen(o => !o)}
                className={`${circleBtn} ${btnOff} !w-11 !h-11`}
                aria-label="Más opciones"
                aria-expanded={menuOpen}
              >
                <FiMoreHorizontal size={22} />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-64 rounded-[14px] bg-[rgba(30,30,30,0.85)] backdrop-blur-2xl backdrop-saturate-150 shadow-2xl overflow-hidden divide-y divide-white/10" role="menu">
                  <button role="menuitem" onClick={toggleHideSelf} className="w-full flex items-center gap-3 px-4 py-3 text-left text-[17px] text-white hover:bg-white/10 active:bg-white/15" style={{ minHeight: 44 }}>
                    {hideSelf ? <FiEye size={16} /> : <FiEyeOff size={16} />}
                    {hideSelf ? 'Mostrar mi imagen' : 'Ocultar mi imagen'}
                  </button>
                  {partnerId && (
                    <button role="menuitem" onClick={() => { setMenuOpen(false); safety.openReport() }} className="w-full flex items-center gap-3 px-4 py-3 text-left text-[17px] text-white hover:bg-white/10 active:bg-white/15" style={{ minHeight: 44 }}>
                      <FiFlag size={16} /> Reportar a {partnerName || 'este usuario'}
                    </button>
                  )}
                  {typeof document !== 'undefined' && document.pictureInPictureEnabled && (
                    <button role="menuitem" onClick={enterFloatingWindow} className="w-full flex items-center gap-3 px-4 py-3 text-left text-[17px] text-white hover:bg-white/10 active:bg-white/15" style={{ minHeight: 44 }}>
                      <FiExternalLink size={16} /> Ventana flotante
                    </button>
                  )}
                  <button role="menuitem" onClick={() => { toggleFullscreen(); setMenuOpen(false) }} className="w-full flex items-center gap-3 px-4 py-3 text-left text-[17px] text-white hover:bg-white/10 active:bg-white/15" style={{ minHeight: 44 }}>
                    {isFullscreen ? <FiMinimize size={16} /> : <FiMaximize size={16} />}
                    {isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Avisos de red y mensajes cortos, debajo de la barra de arriba */}
        {!connecting && (
          <div className="absolute inset-x-0 z-20 flex flex-col items-center gap-2 pointer-events-none" style={{ top: 'calc(env(safe-area-inset-top, 0px) + 58px)' }}>
            {reconnecting && (
              <div role="status" className="flex items-center gap-2 rounded-full bg-amber-500/90 px-3 py-1.5 text-sm font-semibold text-black">
                <div className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin" /> Reconectando…
              </div>
            )}
            {!reconnecting && weakConnection && (
              <div role="status" className="flex items-center gap-2 rounded-full bg-amber-500/90 px-3 py-1.5 text-sm font-semibold text-black">
                <FiWifiOff size={14} /> Conexión débil
              </div>
            )}
            {toast && (
              <button
                key={toast.id}
                onClick={() => setToast(null)}
                role="status"
                className="pointer-events-auto max-w-[85%] rounded-full bg-gray-900/95 px-4 py-2 text-center text-sm font-medium text-white shadow-xl"
              >
                {toast.text}
              </button>
            )}
          </div>
        )}

        {/* Chat de la conversación sobre el video */}
        {showChatBlock && chatShown && (
          <CallChatOverlay
            messages={chat.messages}
            isMine={chat.isMine}
            partnerName={partnerName}
            partnerTyping={chat.partnerTyping}
            open={chatOpen}
            reduceMotion={reduceMotion}
            onRetry={chat.retry}
          />
        )}

        {/* Barra de escribir (reemplaza a los controles mientras el chat está abierto) */}
        {showChatBlock && chatOpen && (
          <>
            <div
              className="absolute inset-x-0 mx-auto max-w-[640px] z-20 flex items-center gap-2 px-3 overflow-x-auto"
              style={{ bottom: 'calc(4.25rem + env(safe-area-inset-bottom, 0px))' }}
            >
              {quickGifts.map((g) => {
                const affordable = (wallet.coins ?? Infinity) >= g.coins
                return (
                  <button
                    key={g.id}
                    onClick={() => affordable && setConfirmGift(g)}
                    disabled={!affordable}
                    className={`shrink-0 flex items-center gap-1 rounded-full bg-amber-400/25 backdrop-blur-md border border-amber-300/40 px-3 py-1.5 text-sm font-semibold text-white ${affordable ? '' : 'opacity-45'}`}
                    aria-label={`Enviar ${g.name}, ${g.coins} coins`}
                    style={{ minHeight: 36 }}
                  >
                    <span aria-hidden="true">{g.emoji}</span> {g.coins}
                  </button>
                )
              })}
              <button
                onClick={openGiftSheet}
                className="shrink-0 flex items-center gap-1 rounded-full bg-white/20 backdrop-blur-md px-3 py-1.5 text-sm font-semibold text-white"
                style={{ minHeight: 36 }}
              >
                <FiGift size={14} /> Más regalos
              </button>
            </div>
            <div
              className="absolute inset-x-0 mx-auto max-w-[640px] z-20 flex items-center gap-2 px-3"
              style={{ bottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
            >
              <button onClick={closeChat} className={`${circleBtn} ${btnOff} !w-11 !h-11`} aria-label="Cerrar el chat">
                <FiChevronDown size={22} />
              </button>
              <div className="flex flex-1 items-center gap-2 rounded-full bg-black/55 backdrop-blur-md px-4 border border-white/10">
                <input
                  ref={inputRef}
                  value={draft}
                  maxLength={MAX_MESSAGE_LENGTH}
                  onChange={(e) => { const v = e.target.value.slice(0, MAX_MESSAGE_LENGTH); setDraft(v); chat.handleInput(v) }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitDraft() } }}
                  placeholder="Escribe un mensaje…"
                  aria-label="Escribe un mensaje"
                  enterKeyHint="send"
                  className="flex-1 bg-transparent py-3 text-sm text-white placeholder-white/50 outline-none"
                />
                <button onClick={openGiftSheet} className="text-white/90" aria-label="Enviar un regalo" style={{ minWidth: 28, minHeight: 44 }}>
                  <FiGift size={20} />
                </button>
              </div>
              <button
                onClick={submitDraft}
                disabled={!draft.trim()}
                onMouseDown={(e) => e.preventDefault()} // el teclado sigue abierto al enviar
                className={`${circleBtn} !w-9 !h-9 ${draft.trim() ? 'bg-pink-500 text-white' : 'bg-white/20 text-white/50'}`}
                aria-label="Enviar mensaje"
              >
                <FiSend size={20} />
              </button>
            </div>
          </>
        )}

        {/* Controles de abajo: micrófono · cámara · cambiar cámara · chat · regalo · colgar */}
        {!chatOpen && (
          <div
            className={`absolute left-0 right-0 flex justify-center z-20 transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] ${controlsVisible || connecting ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'}`}
            style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
          >
          <div className="flex items-center justify-center gap-2.5 max-[380px]:gap-2 sm:gap-3 px-3 max-w-full">
            <button
              onClick={() => { toggleMic(); bumpControls() }}
              className={`${circleBtn} ${isMicEnabled ? btnOff : btnOn}`}
              aria-label={isMicEnabled ? 'Silenciar micrófono' : 'Activar micrófono'}
            >
              {isMicEnabled ? <FiMic size={22} /> : <FiMicOff size={22} />}
            </button>

            <button
              onClick={() => { toggleCamera(); bumpControls() }}
              className={`${circleBtn} ${isCameraEnabled ? btnOff : btnOn}`}
              aria-label={isCameraEnabled ? 'Apagar cámara' : 'Encender cámara'}
            >
              {isCameraEnabled ? <FiVideo size={22} /> : <FiVideoOff size={22} />}
            </button>

            {canSwitchCamera && (
              <button
                onClick={() => { switchCamera(); bumpControls() }}
                className={`${circleBtn} ${btnOff}`}
                aria-label="Cambiar de cámara"
              >
                <FiRepeat size={22} />
              </button>
            )}

            <button
              onClick={() => { openChat(); bumpControls() }}
              className={`${circleBtn} ${btnOff} relative`}
              aria-label={chat.unseen > 0 ? `Chat, ${chat.unseen} mensajes sin ver` : 'Chat'}
            >
              <FiMessageCircle size={22} />
              {chat.unseen > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-pink-500 px-1 text-[11px] font-bold leading-5 text-white text-center">
                  {unseenBadge}
                </span>
              )}
            </button>

            <button
              onClick={() => { openGiftSheet(); bumpControls() }}
              className={`${circleBtn} ${btnOff}`}
              aria-label="Enviar un regalo"
            >
              <FiGift size={22} className="text-[#FFCC00]" />
            </button>

            <button
              onClick={() => { setShowHangupConfirm(true); bumpControls() }}
              className="w-[58px] h-[58px] max-[380px]:w-[52px] max-[380px]:h-[52px] shrink-0 rounded-full bg-[#FF3B30] hover:bg-[#ff4f45] flex items-center justify-center transition-[transform,background-color] duration-200 ease-[cubic-bezier(0.32,0.72,0,1)] active:scale-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              aria-label="Colgar"
            >
              <FiPhoneOff size={26} className="text-white" />
            </button>
          </div>
          </div>
        )}

        {/* Celebración de regalo sobre el video, sin oscurecerlo */}
        {celebration && (
          <CallGiftCelebration
            gift={celebration.gift}
            kind={celebration.kind}
            reduceMotion={reduceMotion}
            onDone={() => setCelebration(null)}
          />
        )}

        {/* Confirmación de un regalo rápido: menos pasos que el catálogo, pero sin gastar por un roce */}
        {confirmGift && (
          <div className="absolute inset-0 z-30 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={() => setConfirmGift(null)}>
            <div className="w-full max-w-xs rounded-2xl bg-gray-900 border border-white/10 p-5 text-center shadow-2xl" role="dialog" aria-label={`¿Enviar ${confirmGift.name}?`} onClick={(e) => e.stopPropagation()}>
              <p className="text-white text-lg font-semibold mb-4">¿Enviar {confirmGift.name}?</p>
              <button
                onClick={() => sendQuickGift(confirmGift)}
                className="w-full mb-2 rounded-full bg-pink-500 py-3 text-sm font-semibold text-white"
              >
                Enviar {confirmGift.emoji} por {confirmGift.coins} coins
              </button>
              <button onClick={() => setConfirmGift(null)} className="w-full rounded-full bg-gray-700 py-3 text-sm font-semibold text-white">
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Confirmación antes de colgar */}
        {showHangupConfirm && (
          <div
            className="absolute inset-0 z-30 bg-black/60 flex items-center justify-center px-6"
            onClick={(e) => { e.stopPropagation(); setShowHangupConfirm(false) }}
          >
            <div
              className="w-full max-w-xs bg-gray-900 border border-white/10 rounded-2xl p-6 text-center shadow-2xl"
              role="alertdialog"
              aria-label="¿Terminar la videollamada?"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="text-white text-lg font-semibold mb-1">¿Terminar la videollamada?</p>
              <p className="text-gray-400 text-sm mb-5">La llamada se cerrará para ambos.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowHangupConfirm(false)}
                  className="flex-1 py-2.5 rounded-full bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold"
                >
                  Seguir en la llamada
                </button>
                <button
                  onClick={hangUp}
                  className="flex-1 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold"
                >
                  Colgar
                </button>
              </div>
            </div>
          </div>
        )}
      </>)}

      {/* Hoja de regalos (la misma del chat normal) y reportar: por encima de la llamada */}
      <GiftModal
        isOpen={giftSheetOpen}
        onClose={() => setGiftSheetOpen(false)}
        userCoins={coinsBalance}
        recipientId={partnerId}
        chatId={chatId}
        authToken={authToken}
        onSuccess={afterGiftSent}
      />
      {safety.modals}
    </div>
  )
}
